import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';

import { inject } from '@angular/core';
import { IResponse } from '../../models/apiResponse';
import { ApiFilter, ApiRequestOptions, ApiResponse, buildApiHttpParams } from './crud-types';

/**
 * Abstract generic CRUD service to interact with ncrudify APIs.
 * @template T The entity type.
 * @template ID The entity identifier type (default number | string).
 * @template CreateDTO The DTO for creation (default T).
 * @template UpdateDTO The DTO for updates (default Partial<T>).
 */
export abstract class BaseCrudService<
  T,
  ID extends number | string = number | string,
  CreateDTO = T,
  UpdateDTO = Partial<T>
> {
  protected http = inject(HttpClient);

  /**
   * The base API path for this resource (e.g., '/companies', '/users').
   * Must be implemented by the concrete class.
   */
  protected abstract getBaseUrl(): string;

  /**
   * Optionally configure common headers for all requests.
   * Useful for `Content-Type`, but authentication is usually handled by interceptors.
   */
  protected getCommonHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      // 'Accept': 'application/json' // A menudo útil
    });
  }

  /**
   * Retrieves multiple resources with optional query parameters.
   * @param {ApiRequestOptions} [options] - Query options (filters, pagination, relations, etc.).
   * @returns {Observable<ApiResponse<T>>} Observable emitting an array of resources or a paginated response.
   */
  getMany(options?: ApiRequestOptions): Observable<ApiResponse<T>> {
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    return this.http.get<ApiResponse<T>>(this.getBaseUrl(), {
      params,
      headers,
    });
  }

  /**
   * Retrieves a specific resource by its ID.
   * @param {ID} id - The ID of the resource.
   * @param {ApiRequestOptions} [options] - Query options (e.g., 'join' or 'fields').
   * @returns {Observable<T>} Observable emitting the found resource.
   */
  getOne(id: ID, options?: ApiRequestOptions): Observable<T> {
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    return this.http.get<T>(`${this.getBaseUrl()}/${id}`, { params, headers });
  }

  /**
   * Creates a new resource.
   * @param {CreateDTO} dto - The data transfer object containing resource data.
   * @param {ApiRequestOptions} [options] - Query options (rarely used in POST).
   * @returns {Observable<T>} Observable emitting the created resource.
   */
  createOne(dto: CreateDTO, options?: ApiRequestOptions): Observable<T> {
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    return this.http.post<T>(this.getBaseUrl(), dto, { params, headers });
  }

  /**
   * Creates multiple resources in bulk.
   * @param {CreateDTO[]} dtos - Array of data transfer objects for resource creation.
   * @param {ApiRequestOptions} [options] - Query options.
   * @returns {Observable<T[]>} Observable emitting the created resources.
   */
  createMany(dtos: CreateDTO[], options?: ApiRequestOptions): Observable<T[]> {
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    const payload = { bulk: dtos };
    return this.http.post<T[]>(`${this.getBaseUrl()}/bulk`, payload, {
      params,
      headers,
    });
  }

  /**
   * Partially updates an existing resource (PATCH).
   * @param {ID} id - The ID of the resource to update.
   * @param {UpdateDTO} dto - Partial data transfer object with fields to update.
   * @param {ApiRequestOptions} [options] - Query options.
   * @returns {Observable<T>} Observable emitting the updated resource.
   */
  updateOne(
    id: ID,
    dto: UpdateDTO,
    options?: ApiRequestOptions
  ): Observable<T> {
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    return this.http.patch<T>(`${this.getBaseUrl()}/${id}`, dto, {
      params,
      headers,
    });
  }

  /**
   * Replaces an existing resource entirely (PUT).
   * @param {ID} id - The ID of the resource to replace.
   * @param {CreateDTO} dto - Data transfer object with the new resource data.
   * @param {ApiRequestOptions} [options] - Query options.
   * @returns {Observable<T>} Observable emitting the replaced resource.
   */
  replaceOne(
    id: ID,
    dto: CreateDTO,
    options?: ApiRequestOptions
  ): Observable<T> {
    // Nota: Usamos CreateDTO aquí asumiendo que PUT requiere un objeto completo, similar a Create.
    // Podría ser T directamente o un DTO específico para Replace.
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    return this.http.put<T>(`${this.getBaseUrl()}/${id}`, dto, {
      params,
      headers,
    });
  }

  /**
   * Deletes a specific resource.
   * @param {ID} id - The ID of the resource to delete.
   * @param {ApiRequestOptions} [options] - Query options.
   * @returns {Observable<void | T>} Observable that completes when the deletion is done or emits the deleted resource.
   */
  deleteOne(id: ID, options?: ApiRequestOptions): Observable<void | T> {
    const params = buildApiHttpParams(options);
    const headers = this.getCommonHeaders();
    // Especificamos el tipo de respuesta esperado como 'void | T'
    // Si el backend retorna el objeto, será T, si no, será null (mapeado a void implicitamente por la finalización del observable).
    // HttpClient por defecto interpreta una respuesta 200 OK sin cuerpo como `null`.
    // Para ser más explícitos sobre no esperar cuerpo, se podría usar { observe: 'response' }
    // y luego mapear, pero esto complica el tipo de retorno. Dejamos `void | T`
    //    // Convertir ID a número si es posible
    return this.http.delete<void | T>(`${this.getBaseUrl()}/${id}`, {
      params,
      headers,
    });
  }

  /**
   * Updates a single field of a resource.
   * @template K
   * @param {ID} id - The ID of the resource.
   * @param {K} field - The name of the field to update.
   * @param {UpdateDTO[K]} value - The new value for the field.
   * @param {ApiRequestOptions} [options] - Query options.
   * @returns {Observable<T>} Observable emitting the updated resource.
   */
  updateSingleField<K extends keyof UpdateDTO>(
    id: ID,
    field: K,
    value: UpdateDTO[K],
    options?: ApiRequestOptions
  ): Observable<T> {
    // Creamos un objeto parcial dinámicamente
    // CORRECCIÓN: Usar doble aserción (as unknown as UpdateDTO)
    const partialDto = { [field]: value } as unknown as UpdateDTO;

    // Reutilizamos updateOne
    return this.updateOne(id, partialDto, options);
  }

/**
 * Updates multiple resources matching a filter.
 * @param {UpdateDTO} dto - Data transfer object with fields to update.
 * @param {ApiRequestOptions} options - Options including filter criteria.
 * @returns {Observable<T[]>} Observable emitting the updated resources.
 */
updateMany(updateDto: UpdateDTO, options: ApiRequestOptions): Observable<T[]> {
  const headers = this.getCommonHeaders();
  const filter = this.convertApiFiltersToNcrudifyFilter(options.filter || []);
  const payload = { 
    filter, 
    updateDto 
  };
    
  
  return this.http.patch<T[]>(`${this.getBaseUrl()}/bulk`, payload, {
    headers,
  });
}
  
  /**
   * Deletes multiple resources matching a filter.
   * @param {any} filter - Criteria to select resources for deletion.
   * @returns {Observable<void>} Observable that completes when the deletion is done.
   */

  deleteMany(filter: any): Observable<void> {
    const headers = this.getCommonHeaders();
    return this.http.request<void>('delete', `${this.getBaseUrl()}/bulk`, {
      headers,
      body: { filter },
    });
  }
  /**
   * Converts ApiFilter array to a filter format compatible with ncrudify
   * @param {ApiFilter[]} filters - Array of ApiFilter objects
   * @returns {object} Filter object in a format ncrudify expects
   * @private
   */
  private convertApiFiltersToNcrudifyFilter(filters: ApiFilter[]): any {
    if (!filters || !filters.length) return {};
    
    const result: any = {};
    
    filters.forEach(filter => {
      const { field, operator, value } = filter;
      
      // Handle special case for 'in' operator which needs array values
      if (operator === 'in' && typeof value === 'string') {
        result[field] = { $in: value.split(',') };
        return;
      }
      
      switch (operator) {
        case 'eq':
          // For equality, use direct value assignment
          result[field] = value;
          break;
        case 'ne':
          result[field] = { $ne: value };
          break;
        case 'gt':
          result[field] = { $gt: value };
          break;
        case 'gte':
          result[field] = { $gte: value };
          break;
        case 'lt':
          result[field] = { $lt: value };
          break;
        case 'lte':
          result[field] = { $lte: value };
          break;
        case 'starts':
          result[field] = { $regex: `^${value}`, $options: 'i' };
          break;
        case 'ends':
          result[field] = { $regex: `${value}$`, $options: 'i' };
          break;
        case 'cont':
          result[field] = { $regex: value, $options: 'i' };
          break;
        case 'excl':
          result[field] = { $not: { $regex: value, $options: 'i' } };
          break;
        case 'notin':
          result[field] = { $nin: value.split(',') };
          break;
        case 'isnull':
          result[field] = { $exists: false };
          break;
        case 'notnull':
          result[field] = { $exists: true };
          break;
        case 'between':
          if (typeof value === 'string' && value.includes(',')) {
            const [min, max] = value.split(',');
            result[field] = { $gte: min, $lte: max };
          }
          break;
        default:
          result[field] = value;
      }
    });
    
    return result;
  }
}
